<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue'
import { Chart, registerables } from 'chart.js'
import { getFontSize, getChartFontSize, numberFormatter } from '@/js/customChart.js'

Chart.register(...registerables)

// props
const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  data: {
    type: Object,
    default: () => ({})
  },
  additionalData: {
    type: Object,
  },
  options: {
    type: Object,
  },
  width: {
    type: Number,
    default: 600
  },
  height: {
    type: Number,
    default: 600
  },
  type : {
    type :String,
  }

})

// data - ref
const MyChart = ref(null)

// computed
// doughnut chart center text
const chartPlugins = computed(() => {  
  const plugins = []
  const innerLabel = {
    id: 'innerLabel',
    afterDatasetDraw(chart, args, pluginOptions) {
      const { ctx } = chart;
      const meta = args.meta;
      const xCoor = meta.data[0].x;
      const yCoor = meta.data[0].y;

      const data = [
        {
          offset: -getChartFontSize(chart.canvas.width, 4) * 1.5,
          value: `${numberFormatter(props.additionalData.centerValue[0])}억원`,
        },
        {
          offset: 0,
          value: `${props.additionalData.centerValue[1]}% vs ${props.title.includes('GM') ? 'Rev.' : 'Quota'}`,
        },
        {
          offset: getChartFontSize(chart.canvas.width, 4) * 1.5,
          value: `WoW`
        },
      ]
      data.forEach(item => {
        ctx.save()
        ctx.textAlign = 'center';
        if (item.value === 'WoW ') {
          ctx.textAlign = 'end';
        }
        ctx.fillStyle = '#000'
        ctx.font = `${getChartFontSize(chart.canvas.width, 4)}px suit-m`;
        ctx.fillText(item.value, xCoor, yCoor + item.offset);
        ctx.restore(); 
      })
      const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
      const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
      const text = `${numberFormatter(props.additionalData.centerValue[2]) || 0}억원`;
      const textWidth = ctx.measureText(text).width;

      ctx.save();
      ctx.textAlign = 'start';
      ctx.fillStyle = props.additionalData.centerValue[2] > 0 ? 'royalblue' : 'red';
      ctx.font = `${getChartFontSize(chart.canvas.width, 4)}px suit-m`;

      // 텍스트 위치 조정 (텍스트 너비 고려)
      const adjustedOffset = data.at(-1).offset + getChartFontSize(chart.canvas.width, 4);
      ctx.fillText(text, centerX - textWidth /1.5, centerY + adjustedOffset);
      ctx.restore();
    },
  }

  const dataLabel = {
    id: 'dataLabel',
    afterDatasetDraw(chart, args, pluginOptions) {
      const { ctx } = chart;
      const meta = args.meta;
      meta.data.forEach((data, index) => {
        const { x, y } = data.tooltipPosition()
        // const x = data.x
        // const y = data.y + (data.height / 2)
        const value = `${chart.data.datasets[0].data[index]}억`
        const value2 = `${props.additionalData.dataPer[index]}%`
        ctx.save()
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle'
        ctx.fillStyle = '#000'
        ctx.font = `${getChartFontSize(chart.canvas.width, 4)}px suit-m`;
        ctx.fillText(value, x, y);
        ctx.fillText(value2, x, y + getChartFontSize(chart.canvas.width, 4) * 1.2);
        ctx.restore()
      })
    },
  }

  const dataLabelOutside = {
    id: 'dataLabelOutside',
    afterDatasetDraw(chart, args, pluginOptions) {
      const { ctx, chartArea: { top, bottom, left, right, width, height }, options: { layout: { padding } } } = chart;
      const meta = args.meta;
      meta.data.forEach((data, index) => {
        const { x, y } = data.tooltipPosition()

        const halfWidth = width / 2
        const halfHeight = height / 2

        const xLine = x >= halfWidth ? x + (width / 20) : x - (width / 20)
        const yLine = y >= halfHeight ? y + (width / 20) : y - (width / 20)
        const extraLine = x >= halfWidth ? +(width / 15) : - (width / 15)

        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(xLine, yLine)
        ctx.lineTo(xLine + extraLine, yLine)
        ctx.stroke()

        const textWidth = ctx.measureText(`${chart.data.datasets[0].data[index]}억`).width
        ctx.font = `${getChartFontSize(chart.canvas.width, 3)}px suit-m` 

        const textXPosition = x >= halfWidth ? 'left' : 'right'
        const plus = x >= halfWidth ? 8 : -8
        const textYPosition = y >= halfHeight ? getChartFontSize(chart.canvas.width, 2.5) * -1.2 : 0
        ctx.textAlign = textXPosition
        ctx.textBaseline = 'middle'
        ctx.fillStyle = getFontColor(props.additionalData.rawData[index])
        ctx.fillText(`${numberFormatter(props.additionalData.realData[index])}억`, xLine + extraLine + plus, yLine + textYPosition)
        ctx.font = `${getChartFontSize(chart.canvas.width, 2.5)}px suit-m` 
        ctx.fillStyle = getFontColor(1)
        ctx.fillText(`${props.additionalData.dataPer[index]}%`, xLine + extraLine + plus, yLine + textYPosition + getChartFontSize(chart.canvas.width, 2.5) * 1.2);

        // const value = `${chart.data.datasets[0].data[index]}억`
        // const value2 = `${props.additionalData.dataPer[index]}%`
        // ctx.save()
        // ctx.textAlign = 'center';
        // ctx.textBaseline = 'middle'
        // ctx.fillStyle = '#000'
        // ctx.font = `${getChartFontSize(chart.canvas.width, 4)}px suit-m`;
        // ctx.fillText(value, x, y);
        // ctx.fillText(value2, x, y + getChartFontSize(chart.canvas.width, 4) * 1.2);
        // ctx.restore()
      })
    },
  }

//   const customTooltipPlugin = {
//     id: 'customTooltipPlugin',
//     beforeRender: function(chart) {  // beforeRender 또는 다른 훅에서 설정
//         const tooltip = chart.options.plugins.tooltip;
//         // 툴팁의 콜백을 설정하여 값을 변경
//         tooltip.callbacks = {
//             label: function(context,index) {
//               console.log('context.dataset.label',context.dataset)
//                 let label =[]
//                 if(!props.title.includes('Monthly Rev. & GM FCST')){
//                   console.log('{props.additionalData?.realData',props.additionalData?.realData)
                  
//                     label.push(`${props.additionalData?.realData[context.dataIndex]}억`) 
//                     label.push(`${props.additionalData?.dataPer[context.dataIndex]} %`);
                  
//                   // label.push(`${props.additionalData?.realData[context.dataIndex]}억`) 
//                   // label.push(`${props.additionalData?.dataPer[context.dataIndex]} %`);
//                 }else{
//                   label += `${props.additionalData?.realData[context.dataIndex]}억
//                   ${props.additionalData?.dataPer[context.dataIndex]} %`;
//                 }
//                 console.log('label',label)
//                 return label
//             }
//         };
//     }
// };

  plugins.push(innerLabel)
  plugins.push(dataLabelOutside)
  //plugins.push(customTooltipPlugin)
  return plugins
})

const chartOptions = computed(() => {
  return
})

// watch
watch([() => props.data, () => props.options,() => props.data], (newValue, oldValue) => {
  if (newValue) {
    initChart()
  }
})

// lifecycle
onMounted(() => {
  initChart()
})

onBeforeUnmount(() => {
  Chart.getChart(props.title)?.destroy()
})

// methods
function initChart() {
  if (Chart.getChart(props.title)) {
    Chart.getChart(props.title)?.destroy()
  }
  new Chart(MyChart.value, {
    type: 'doughnut',
    plugins: chartPlugins.value,
    data: props.data,
    options: props.options
  })
}

function getFontColor(value) {
  return value <= 0 ? 'red' : 'black'
}
</script>

<template>
  <article class="canvas-wrap" :style="{ width: `${width}px`, height: `${height}px` }">
    <canvas :id="title" ref="MyChart" />
  </article>
</template>

<style scoped>
.canvas-wrap {
  display: flex;
  justify-content: center;
  align-items: center
}
</style>
